import { css, cx } from '@emotion/css';
import { DraggableCompareByService } from 'client/js/common/components/core/PureQueryBuilder/DraggableCompareBy/DraggableCompareBy.service';
import * as R from 'ramda';
import * as React from 'react';
import type { LegacyRef } from 'react';
import ReactSelect, { Creatable } from 'react-select';
import type { TitleProps } from '../../types/titleTypes';
import Title from './Title';
import MenuList from './dropdown/MenuList';
import { getClearable } from './dropdown/compat';
import wrappedComponents from './dropdown/components';
import { customStyles } from './dropdown/styles';
const REQUIRED_FOOTNOTE = '*This field is required';

/**
 * For all property options, see: https://github.com/JedWatson/react-select
 *
 * Props that we want to handle instead of directly passing into react-select:
 */
export const HANDLED_PROPS = [
  'components',
  'disabled',
  'testid',
  'onChange',
  'onOpen',
  'outerClassName',
  'label',
  'labelClassName',
  'requiredNote',
  'childRef',
  'backspaceRemovesValue',
  'styles',
] as const;
export type SelectEvent = {
  value: string;
  label: string;
};
export type SelectEventAction = {
  action: string;
};
export type DropdownProps = {
  name?: string;
  styles?: any;
  components?: any;
  onInputChange?: (args: any) => void;

  /**
   * For regular dropdowns, e will be SelectEvent.
   * If dropdown is a multiselect, e will be an array of SelectEvents.
   */
  onChange: (e: any, action: SelectEventAction) => void;

  /**
   * Required for QA testing, applied to outer div
   */
  testid: string;

  /**
   * Allow creation of new options by typing in select box
   */
  creatable?: boolean;

  /**
   * Optional callback invoked when select component is opened
   */
  onOpen?: () => void;

  /**
   * className applied to outer div
   */
  outerClassName?: string;

  /**
   * Show "*This is required" below the dropdown if true
   */
  requiredNote?: boolean;
  shouldKeyDownEventCreateNewOption?: (arg0: Record<string, any>) => boolean;
  inputProps?: {
    onPaste: (arg0: React.ClipboardEvent<any>) => void;
  };
  childRef?: ChildRef | null | undefined;
  ref?: LegacyRef<Dropdown>;
  menuRenderer?: () => void;
  noResultsText?: string | null | undefined;
  multi?: boolean;
  clearable?: boolean;
  className?: string;
  placeholder?: string | React.ReactNode;
  value: any;
  disabled?: boolean;
  optionRenderer?: (option: any) => any;
  renderOption?: (data: any, Component: any) => any;
  showCustomAction?: boolean;
  customActionText?: string;
  onCustomAction?: () => any;
  borderColor?: string;
  options?: any[];
  backspaceRemoves?: boolean;
  searchable?: boolean;
  closeMenuOnSelect?: boolean;
  isLoading?: boolean;
  draggable?: boolean;
} & TitleProps;
type ChildRef = {
  pasteInput: unknown;
};
type ChildProps = Omit<DropdownProps, (typeof HANDLED_PROPS)[number]>;

export const getValue = (options: any[] = [], value?: any) => {
  if (value === undefined) {
    return value;
  }

  if (value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.map((single) => getValue(options, single)).filter(Boolean);
  }

  if (value instanceof Object) {
    return value;
  }

  return findValueInOptions(value, options);
};
export function findValueInOptions(value: any, options: any[]) {
  const foundValues = options.filter(Boolean).map((option) => {
    if (option.options) {
      return findValueInOptions(value, option.options);
    }

    return option.value === value ? option : undefined;
  });
  return foundValues.find(Boolean);
}

class Dropdown extends React.PureComponent<DropdownProps> {
  _onOpen = () => {
    this.props.onOpen && this.props.onOpen();
  };

  /*
   * For regular dropdowns, e will be type: SelectEvent obj.
   * If dropdown is a multiselect, e will be type: an array of SelectEvent objs.
   */
  _onChange = (e: SelectEvent | Array<SelectEvent>, action: SelectEventAction) => {
    this.props.onChange(e, action);
  };

  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  _handleIsValidNewOption = (inputValue, selectValue, selectOptions) => {
    const exactValueExists = selectOptions.find((el) => el.value === inputValue);
    const valueIsNotEmpty = inputValue.trim().length;
    return !exactValueExists && valueIsNotEmpty;
  };

  render() {
    const props = this.props;
    const Component = props.creatable ? Creatable : ReactSelect;
    const childProps = R.omit(HANDLED_PROPS, { ...props });

    if (props.creatable && props.childRef) {
      // paste requires a reference to react-select component internals
      childProps.ref = (ref) => ((childProps as ChildProps & DropdownProps['childRef']).pasteInput = ref);
    }

    const getOption = (): any => {
      const optionRenderer: any = props.optionRenderer;

      if (optionRenderer) {
        return (optionProps: any) => {
          const { cx, getStyles } = optionProps;
          const className = cx(css(getStyles('option', optionProps)));
          return (
            <div ref={optionProps.innerRef} className={className} {...optionProps.innerProps}>
              {optionRenderer(optionProps.data)}
            </div>
          );
        };
      } else if (props?.renderOption) {
        return (optionProps: any) => {
          const { cx, getStyles } = optionProps;
          const className = cx(css(getStyles('option', optionProps)));
          return (
            <div ref={optionProps.innerRef} className={className} {...optionProps.innerProps}>
              {props?.renderOption?.(optionProps, wrappedComponents.Option)}
            </div>
          );
        };
      } else if (props.components && props.components.Option) {
        return props.components.Option;
      } else {
        return (optionProps: any) => {
          return <wrappedComponents.Option {...optionProps} />;
        };
      }
    };

    const className = cx(
      props.className,
      'Select',
      props.multi ? 'Select--multi' : 'Select--single',
      props.disabled ? 'is-disabled' : undefined
    );

    let value = getValue(props.options, props.value);

    if (props.draggable) {
      value = DraggableCompareByService.addIndex(value);
    }

    return (
      <div className={cx(props.outerClassName)} data-testid={props.testid}>
        <Title
          label={props.label}
          description={props.description}
          hoverableTooltip={props.hoverableTooltip}
          tooltip={props.tooltip}
          labelClassName={props.labelClassName}
        />
        <Component
          {...childProps}
          createOptionPosition={props.creatable && 'first'}
          isSearchable={props.searchable}
          styles={{ ...customStyles, ...(props.styles || {}) }}
          className={className}
          classNamePrefix="Select"
          backspaceRemovesValue={props.backspaceRemoves}
          isDisabled={props.disabled}
          value={value}
          components={{ ...wrappedComponents, ...(this.props.components || {}), Option: getOption(), MenuList }}
          isClearable={getClearable(props)}
          isMulti={props.multi}
          isOptionDisabled={(option) => option.disabled}
          closeMenuOnSelect={props.closeMenuOnSelect || !props.multi} // force dropdown to remain open for multiselect dropdowns
          onChange={this._onChange}
          onMenuOpen={this._onOpen}
          isValidNewOption={this._handleIsValidNewOption}
        />
        {props.requiredNote && <span className="input-control__note h_ta-start">{REQUIRED_FOOTNOTE}</span>}
      </div>
    );
  }
}

export default Dropdown;
